/* Atlas Lab Banner — sticky image with two full-height copy panels.
 * The image stays pinned while the section track scrolls, then naturally
 * returns to normal flow when the track ends.
 */
class AtlasLabBanner extends HTMLElement {
  constructor() {
    super();
    this.ticking = false;
    this.trackTop = 0;
    this.distance = 1;
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.update = this.update.bind(this);
  }

  connectedCallback() {
    if (this.dataset.atlasReady === 'true') return;
    this.dataset.atlasReady = 'true';

    this.track = this.querySelector('.atlas-banner__track');
    this.scene = this.querySelector('.atlas-banner__scene');
    this.mediaInner = this.querySelector('.atlas-banner__media-inner');
    this.titles = this.querySelector('.atlas-banner__titles');
    this.desc = this.querySelector('.atlas-banner__desc');

    if (!this.track || !this.scene) return;

    this.titleFadeStart = this.readNum(this.dataset.titleFadeStart, 0.08);
    this.titleFadeEnd = this.readNum(this.dataset.titleFadeEnd, 0.38);
    this.descFadeStart = this.readNum(this.dataset.descFadeStart, 0.5);
    this.descFadeEnd = this.readNum(this.dataset.descFadeEnd, 0.82);
    this.imageScale = this.readNum(this.dataset.imageScale, 1.05);
    this.imageParallax = this.readNum(this.dataset.imageParallax, 24);

    this.measure();
    this.update();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    const img = this.querySelector('.atlas-banner__img');
    if (img && !img.complete) {
      img.addEventListener('load', this.onResize, { once: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  onScroll() {
    this.requestTick();
  }

  onResize() {
    this.measure();
    this.requestTick();
  }

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  }

  measure() {
    const baseOffset = this.readNum(this.dataset.headerOffset, 60);
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    const headerGroupHeight = headerGroup ? Math.round(headerGroup.getBoundingClientRect().height) : 0;
    const extraOffset = Math.max(headerGroupHeight - baseOffset, 0);

    this.style.setProperty('--atlas-header-extra-offset', `${extraOffset}px`);

    const rect = this.track.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    this.trackTop = rect.top + scrollY;
    this.distance = Math.max(this.track.offsetHeight - this.scene.offsetHeight, 1);
    this.style.setProperty('--atlas-scene-height', `${this.scene.offsetHeight}px`);
  }

  update() {
    this.ticking = false;

    const scrollY = window.scrollY || window.pageYOffset;
    const progress = this.clamp((scrollY - this.trackTop) / this.distance, 0, 1);

    const titlesOpacity = 1 - this.smooth01(
      (progress - this.titleFadeStart) / Math.max(this.titleFadeEnd - this.titleFadeStart, 0.0001)
    );
    const descOpacity = this.smooth01(
      (progress - this.descFadeStart) / Math.max(this.descFadeEnd - this.descFadeStart, 0.0001)
    );

    this.style.setProperty('--atlas-titles-opacity', titlesOpacity.toFixed(3));
    this.style.setProperty('--atlas-desc-opacity', descOpacity.toFixed(3));

    if (this.mediaInner) {
      const eased = this.smooth01(progress);
      const scale = this.lerp(1, this.imageScale, eased);
      const shift = this.lerp(0, -this.imageParallax, eased);
      this.style.setProperty('--atlas-media-scale', scale.toFixed(4));
      this.style.setProperty('--atlas-media-shift', `${shift.toFixed(2)}px`);
    }

    if (this.titles) this.titles.style.pointerEvents = titlesOpacity <= 0.01 ? 'none' : '';
    if (this.desc) this.desc.style.pointerEvents = descOpacity <= 0.01 ? 'none' : '';
  }

  readNum(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  lerp(from, to, t) {
    return from + (to - from) * t;
  }

  smooth01(value) {
    const t = this.clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  }
}

if (!window.customElements.get('atlas-lab-banner')) {
  window.customElements.define('atlas-lab-banner', AtlasLabBanner);
}
